// <site-header>: mega panels that grow like a blob from their menu item, the full-screen mobile menu,
// hide on scroll down / show on scroll up. Markup and settings: sections/header.liquid.

const html = document.documentElement
const reduced = html.classList.contains('reduced')
const fine = html.classList.contains('fine')
const EASE = 'cubic-bezier(0.16, 1, 0.3, 1)'

/** Reveal an element as a circle growing from (x, y) in its own coordinates. */
const grow = (el, x, y, radius, duration) =>
  reduced ||
  el.animate([{ clipPath: `circle(0px at ${x}px ${y}px)` }, { clipPath: `circle(${radius}px at ${x}px ${y}px)` }], { duration, easing: EASE })

class SiteHeader extends HTMLElement {
  connectedCallback() {
    this.panels = new Map([...this.querySelectorAll('[data-mega]')].map((p) => [p.id, p]))
    this.triggers = [...this.querySelectorAll('[data-panel]')]
    this.mobile = this.querySelector('#MobileMenu')
    this.burger = this.querySelector('.site-header__burger')
    this.open = null
    let openT = 0
    let closeT = 0

    for (const t of this.triggers) {
      const id = t.dataset.panel
      // a link trigger (Verkaufen) navigates on click; its panel only opens on hover. With a mouse or trackpad the
      // panel is usually open from hovering already: a click (a tap on the trackpad) then keeps it open instead of
      // toggling it shut; leaving the header closes it. Touch: the click toggles.
      t.addEventListener('click', () => (t.tagName === 'A' ? this.hide(true) : this.open === id && !fine ? this.hide() : this.show(id, t)))
      if (fine) {
        t.addEventListener('pointerenter', () => {
          clearTimeout(closeT)
          clearTimeout(openT)
          openT = setTimeout(() => this.show(id, t), this.open ? 0 : 90) // hover intent
        })
        t.addEventListener('pointerleave', () => clearTimeout(openT))
      }
    }
    if (fine) {
      // While a panel grows in, its clip-path makes the not-yet-revealed part transparent to the pointer: moving
      // into it early fires pointerleave on the header. So before closing, check where the pointer really is.
      let px = -1
      let py = -1
      addEventListener('pointermove', (e) => ((px = e.clientX), (py = e.clientY)), { passive: true })
      const inside = (el) => {
        const r = el?.getBoundingClientRect()
        return !!r && px >= r.left && px <= r.right && py >= r.top && py <= r.bottom
      }
      const tryHide = () => {
        if (inside(this.panels.get(this.open))) return (closeT = setTimeout(tryHide, 200)) // still over the panel
        this.hide()
      }
      this.addEventListener('pointerleave', () => {
        clearTimeout(openT)
        closeT = setTimeout(tryHide, 200)
      })
      this.addEventListener('pointerenter', () => clearTimeout(closeT))
      // hovering a plain link, the logo or the tools closes an open panel: menus shouldn't linger behind the cursor
      this.querySelectorAll('a.site-header__item, .site-header__logo, .site-header__tools').forEach((el) =>
        el.addEventListener('pointerenter', () => {
          clearTimeout(openT)
          if (this.open) closeT = setTimeout(() => this.hide(), 120)
        })
      )
    }
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return
      if (this.open) {
        const t = this.triggers.find((x) => x.dataset.panel === this.open)
        this.hide()
        t?.focus()
      }
      if (!this.mobile.hidden) this.closeMobile(true)
    })
    document.addEventListener('click', (e) => {
      if (this.open && !this.contains(e.target)) this.hide()
    })
    // e.detail 0 = opened with the keyboard (Enter/Space), not a tap
    this.burger.addEventListener('click', (e) => (this.mobile.hidden ? this.openMobile(e.detail === 0) : this.closeMobile(true)))

    this.setupSearch()

    // Panel images are lazy (closed panels must not cost the first paint). Once the page is idle, fetch them,
    // so a panel never opens with empty discs.
    const warm = () => this.querySelectorAll('.mega img[loading="lazy"]').forEach((img) => (img.loading = 'eager'))
    addEventListener('load', () => ('requestIdleCallback' in window ? requestIdleCallback(warm, { timeout: 3000 }) : setTimeout(warm, 1500)), { once: true })

    // Hide on scroll down, show on scroll up (never while something is open).
    let lastY = scrollY
    addEventListener('scroll', () => {
      const y = scrollY
      if (Math.abs(y - lastY) < 5) return
      this.classList.toggle('is-hidden', y > 240 && y > lastY && !this.open && this.mobile.hidden)
      lastY = y
    }, { passive: true })
  }

  show(id, from) {
    if (this.open === id) return
    if (this.open) this.hide(true)
    const p = this.panels.get(id)
    p.hidden = false
    this.open = id
    this.classList.remove('is-hidden')
    this.triggers.forEach((t) => t.setAttribute('aria-expanded', String(t.dataset.panel === id)))
    const r = from.getBoundingClientRect()
    const box = p.getBoundingClientRect()
    grow(p, r.left + r.width / 2 - box.left, r.top + r.height / 2 - box.top, Math.hypot(box.width, box.height) * 1.05, 700)
    if (!reduced)
      [...p.querySelectorAll('.mega__col > *, .mega__promo, .mega__brand, .mega__card, .mega__row')].forEach((el, i) =>
        el.animate([{ opacity: 0, transform: 'translateY(18px)' }, { opacity: 1, transform: 'none' }], { duration: 600, delay: 80 + i * 25, easing: EASE, fill: 'backwards' })
      )
  }

  hide(instant = false) {
    if (!this.open) return
    const p = this.panels.get(this.open)
    this.open = null
    this.triggers.forEach((t) => t.setAttribute('aria-expanded', 'false'))
    if (instant || reduced) return (p.hidden = true)
    p.animate([{ opacity: 1, transform: 'none' }, { opacity: 0, transform: 'translateY(-8px)' }], { duration: 200, easing: 'ease-in' }).onfinish = () => {
      if (this.open !== p.id) p.hidden = true
    }
  }

  // ---------- Search: Shopify predictive search, results rendered by sections/predictive-search.liquid ----------
  setupSearch() {
    const dlg = this.querySelector('#Search')
    const opener = this.querySelector('[data-search-open]')
    if (!dlg || !opener) return
    const input = dlg.querySelector('input[name="q"]')
    const out = dlg.querySelector('#SearchResults')
    let timer = 0
    let ctrl = null
    const run = () => {
      const q = input.value.trim()
      ctrl?.abort()
      if (!q) return (out.innerHTML = '')
      ctrl = new AbortController()
      const url = `${window.Shopify?.routes?.root || '/'}search/suggest?q=${encodeURIComponent(q)}` +
        '&resources[type]=product,collection,page,article,query&resources[limit]=10&resources[limit_scope]=each' + // each type its own 10 (else 6 shared by all), re-ranked in the section
        '&resources[options][unavailable_products]=last&section_id=predictive-search'
      fetch(url, { signal: ctrl.signal })
        .then((r) => r.text())
        .then((text) => {
          const doc = new DOMParser().parseFromString(text, 'text/html')
          out.innerHTML = doc.querySelector('#shopify-section-predictive-search')?.innerHTML || ''
        })
        .catch(() => {})
    }
    // In a search field the browser's first Esc only clears the text. One Esc should close the overlay.
    input.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return
      e.preventDefault()
      dlg.close()
    })
    input.addEventListener('input', () => {
      clearTimeout(timer)
      timer = setTimeout(run, 180) // debounce: one request per pause in typing
    })
    dlg.querySelectorAll('[data-q]').forEach((b) =>
      b.addEventListener('click', () => {
        input.value = b.dataset.q
        run()
        input.focus()
      })
    )
    const close = () => dlg.open && dlg.close()
    dlg.addEventListener('close', () => {
      html.classList.remove('search-open')
      window.janado?.lenis?.start()
      opener.focus({ preventScroll: true })
    })
    dlg.querySelector('[data-search-close]').addEventListener('click', close)
    dlg.addEventListener('click', (e) => e.target === dlg && close())
    opener.addEventListener('click', (e) => {
      e.preventDefault() // without JS the icon is a plain link to /search
      this.hide(true)
      this.closeMobile(false)
      dlg.showModal()
      html.classList.add('search-open')
      window.janado?.lenis?.stop()
      const r = opener.getBoundingClientRect()
      grow(dlg, r.left + r.width / 2, r.top + r.height / 2, Math.hypot(innerWidth, innerHeight), 700)
      input.focus()
      input.select()
    })
  }

  openMobile(keyboard = false) {
    this.hide(true)
    this.mobile.hidden = false
    this.burger.setAttribute('aria-expanded', 'true')
    html.classList.add('menu-open')
    window.janado?.lenis?.stop()
    const r = this.burger.getBoundingClientRect()
    grow(this.mobile, r.left + r.width / 2, r.top + r.height / 2, Math.hypot(innerWidth, innerHeight), 800)
    if (!reduced)
      [...this.mobile.querySelectorAll('.mobile-menu__inner > *')].forEach((el, i) =>
        el.animate([{ opacity: 0, transform: 'translateY(30px)' }, { opacity: 1, transform: 'none' }], { duration: 700, delay: 100 + i * 40, easing: EASE, fill: 'backwards' })
      )
    // keyboard: straight to the first link. Tap: focus the menu itself, so no link looks selected
    const start = keyboard ? this.mobile.querySelector('a, summary') : this.mobile
    start?.focus({ preventScroll: true })
  }

  closeMobile(focusBurger) {
    if (this.mobile.hidden) return
    this.mobile.hidden = true
    this.burger.setAttribute('aria-expanded', 'false')
    html.classList.remove('menu-open')
    window.janado?.lenis?.start()
    if (focusBurger) this.burger.focus()
  }
}

customElements.get('site-header') || customElements.define('site-header', SiteHeader)

// ---------- Campaign teaser (home page): grows out of the campaign pill after a delay or on first scroll ----------
const pop = document.querySelector('[data-camp-pop]')
if (pop) {
  const KEY = 'janado:camp-pop'
  let closed = false
  try {
    closed = sessionStorage.getItem(KEY) === '1'
  } catch {}
  document.body.append(pop) // out of the header, which slides away on scroll
  const origin = () => {
    // the pill when it's visible, else the burger (narrow screens)
    const el = [...document.querySelectorAll('.site-header__campaign, .site-header__burger')].find((e) => e.offsetParent)
    const r = el?.getBoundingClientRect()
    const p = pop.getBoundingClientRect()
    return r ? [r.left + r.width / 2 - p.left, r.top + r.height / 2 - p.top] : [p.width - 40, -20]
  }
  const show = () => {
    if (closed || !pop.hidden) return
    // never on top of the cookie banner: wait for the decision, then come in a moment later (checked again then)
    const consent = document.getElementById('Consent')
    if (consent?.open) return consent.addEventListener('close', () => setTimeout(show, 1500), { once: true })
    pop.hidden = false
    if (reduced) return
    const [x, y] = origin()
    pop.animate([{ clipPath: `circle(0px at ${x}px ${y}px)` }, { clipPath: `circle(${Math.hypot(pop.offsetWidth, pop.offsetHeight) + Math.abs(y)}px at ${x}px ${y}px)` }], { duration: 900, easing: EASE })
    pop.querySelector('.camp-pop__img')?.animate([{ scale: 0.4, rotate: '-12deg' }, { scale: 1, rotate: '0deg' }], { duration: 1100, easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)', delay: 150, fill: 'backwards' })
  }
  const close = () => {
    closed = true
    try {
      sessionStorage.setItem(KEY, '1')
    } catch {}
    if (reduced) return (pop.hidden = true)
    const [x, y] = origin()
    pop.animate([{ clipPath: `circle(${pop.offsetWidth * 1.5}px at ${x}px ${y}px)` }, { clipPath: `circle(0px at ${x}px ${y}px)` }], { duration: 450, easing: 'ease-in' }).onfinish = () => (pop.hidden = true)
  }
  if (!closed) {
    const delay = Math.max(1, Number(pop.dataset.delay) || 5) * 1000
    const t = setTimeout(show, delay)
    const onScroll = () => scrollY > 160 && (removeEventListener('scroll', onScroll), clearTimeout(t), show())
    addEventListener('scroll', onScroll, { passive: true })
  }
  pop.querySelector('[data-camp-close]').addEventListener('click', close)
  pop.querySelector('a').addEventListener('click', () => {
    try {
      sessionStorage.setItem(KEY, '1')
    } catch {}
  })
  addEventListener('keydown', (e) => e.key === 'Escape' && !pop.hidden && close())
}
